import {
  COUNT_TYPES,
  VIEW,
  VIEW_AD_UNIT_IDS_TAB,
  VIEW_AD_SERVERS_TAB,
  VIEW_NETWORKS_TAB,
  VIEW_PARTNER_GROUPS_TAB,
} from '../../constants';

export const TAB_PERMISSIONS = [
  { permission: VIEW_AD_UNIT_IDS_TAB, tab: COUNT_TYPES.ADUNITID },
  { permission: VIEW_AD_SERVERS_TAB, tab: COUNT_TYPES.ADSERVER },
  { permission: VIEW_NETWORKS_TAB, tab: COUNT_TYPES.NETWORK },
  { permission: VIEW_PARTNER_GROUPS_TAB, tab: COUNT_TYPES.PARTNER_GROUP },
];

export const getFirstPermittedTab = (permissions) => {
  const entry = TAB_PERMISSIONS.find(({ permission }) => permissions.can(VIEW, permission));
  return entry?.tab ?? null;
};

export const hasTabPermission = (permissions, countType) => {
  const entry = TAB_PERMISSIONS.find(({ tab }) => tab === countType);
  return entry ? permissions.can(VIEW, entry.permission) : false;
};

export const STATUS_NAMES = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  SUCCESS_RATE: 'success_%',
  UNSUPPORTED_FORMAT: 'unsupported_format',
  UNSUPPORTED_FORMAT_VPAID: 'unsupported_format_vpaid',
  UNSUPPORTED_NON_SSL: 'unsupported_non_ssl',
  UNPARSEABLE_DOCUMENT: 'unparseable_document',
  UNPARSEABLE_DOCUMENT_NULL_VAST: 'unparseable_document_null_vast',
  UNPARSEABLE_DOCUMENT_PARSE_FAIL: 'unparseable_document_parse_fail',
  INVALID_DOCUMENT: 'invalid_document',
  INVALID_DOCUMENT_NO_CREATIVES: 'invalid_document_no_creatives',
  INVALID_DOCUMENT_INVALID_IMPRESSION_URL: 'invalid_document_invalid_impression_url',
  INVALID_DOCUMENT_NO_TRACKING_EVENTS: 'invalid_document_no_tracking_events',
  INVALID_DOCUMENT_NO_VIDEO_CLICKS: 'invalid_document_no_video_clicks',
  MISSING_ASSET: 'missing_asset',
  MISSING_ASSET_EXTERNAL_AD: 'missing_asset_external_ad',
  MISSING_ASSET_VAST_AD: 'missing_asset_vast_ad',
  INTERNAL_ERROR: 'internal_error',
  INTERNAL_ERROR_INLINE_VAST_PROCESS: 'internal_error_inline_vast_process',
  LOCAL_PENALTY: 'local_penalty',
  ASSET_REJECTION: 'asset_rejection',
  ASSET_PUBLISHER_UNAPPROVED: 'asset_publisher_unapproved',
  PROGRAMMATIC_GUARANTEED_DEAL_MISMATCH: 'programmatic_guaranteed_deal_mismatch',
  MISSING_ASSET_NOT_LINKED_TO_AD: 'missing_asset_not_linked_to_ad',
  SENT: 'sent',
  APP: 'app',

  // New event types (KOALA-1695)
  ASSET_MISSING: 'asset_missing',
  ASSET_MISSING_NOT_LINKED_TO_AD: 'asset_missing_not_linked_to_ad',
  ASSET_REJECTED: 'asset_rejected',
  ASSET_UNQUALIFIED: 'asset_unqualified',
  COMPANION_INVALID_API_FRAMEWORK: 'companion_invalid_api_framework',
  COMPANION_INVALID_TYPE: 'companion_invalid_type',
  COMPANION_MISSING: 'companion_missing',
  COMPANION_REGISTERED_NOT_FOUND: 'companion_registered_not_found',
  FETCH_FAILED: 'fetch_failed',
  INTERNAL_INVALID_VAST_URL: 'internal_invalid_vast_url',
  INTERNAL_NO_VAST_URL: 'internal_no_vast_url',
  INTERNAL_NULL_VAST_FROM_AD_PROVIDER: 'internal_null_vast_from_ad_provider',
  INVALID_DOCUMENT_EMPTY_AD: 'invalid_document_empty_ad',
  INVALID_DOCUMENT_INVALID_CLICKTHROUGH_URL: 'invalid_document_invalid_clickthrough_url',
  INVALID_DOCUMENT_NULL_RAW: 'invalid_document_null_raw',
  INVALID_DOCUMENT_PG_DEAL_MISMATCH: 'invalid_document_pg_deal_mismatch',
  INVALID_DOCUMENT_PG_DEAL_NAME_MISSING: 'invalid_document_pg_deal_name_missing',
  INVALID_DOCUMENT_UNMARSHALLER_EXCEPTION: 'invalid_document_unmarshaller_exception',
  INVALID_DOCUMENT_UNPARSEABLE_DOCUMENT: 'invalid_document_unparseable_document',
  INVALID_DOCUMENT_UNWRAPPING_FAILURE: 'invalid_document_unwrapping_failure',

  // HTTP related
  HTTP_REASON_BAD_GATEWAY: 'http_reason_bad_gateway',
  HTTP_REASON_BAD_REQUEST: 'http_reason_bad_request',
  HTTP_REASON_FORBIDDEN: 'http_reason_forbidden',
  HTTP_REASON_INTERNAL_SERVER_ERROR: 'http_reason_internal_server_error',
  HTTP_REASON_OK: 'http_reason_ok',
  HTTP_REASON_REQUEST_TIMEOUT: 'http_reason_request_timeout',
  HTTP_REASON_SERVICE_UNAVAILABLE: 'http_reason_service_unavailable',
};

export const STATUS_COLORS = {
  [STATUS_NAMES.SUCCESS]: 'rgb(0, 128, 0)', // Green
  [STATUS_NAMES.FAILURE]: 'rgb(255, 0, 0)', // Red
  [STATUS_NAMES.SUCCESS_RATE]: 'rgb(0, 0, 255)', // Blue
  [STATUS_NAMES.UNSUPPORTED_FORMAT]: 'rgb(255, 255, 0)', // Yellow
  [STATUS_NAMES.UNSUPPORTED_FORMAT_VPAID]: 'rgb(128, 0, 128)', // Purple
  [STATUS_NAMES.UNSUPPORTED_NON_SSL]: 'rgb(255, 165, 0)', // Orange
  [STATUS_NAMES.UNPARSEABLE_DOCUMENT]: 'rgb(255, 69, 139)', // Rose
  [STATUS_NAMES.UNPARSEABLE_DOCUMENT_NULL_VAST]: 'rgb(255, 0, 255)', // Magenta
  [STATUS_NAMES.UNPARSEABLE_DOCUMENT_PARSE_FAIL]: 'rgb(0, 128, 128)', // Teal
  [STATUS_NAMES.INVALID_DOCUMENT]: 'rgb(128, 128, 0)', // Olive
  [STATUS_NAMES.INVALID_DOCUMENT_NO_CREATIVES]: 'rgb(255, 69, 0)', // Red-Orange
  [STATUS_NAMES.INVALID_DOCUMENT_INVALID_IMPRESSION_URL]: 'rgb(128, 0, 0)', // Maroon
  [STATUS_NAMES.INVALID_DOCUMENT_NO_TRACKING_EVENTS]: 'rgb(0, 255, 0)', // Lime
  [STATUS_NAMES.INVALID_DOCUMENT_NO_VIDEO_CLICKS]: 'rgb(128, 128, 128)', // Gray
  [STATUS_NAMES.MISSING_ASSET]: 'rgb(0, 0, 128)', // Navy
  [STATUS_NAMES.MISSING_ASSET_EXTERNAL_AD]: 'rgb(128, 0, 255)', // Lavender
  [STATUS_NAMES.MISSING_ASSET_VAST_AD]: 'rgb(0, 255, 128)', // Spring Green
  [STATUS_NAMES.INTERNAL_ERROR]: 'rgb(139, 0, 0)', // Dark Red
  [STATUS_NAMES.INTERNAL_ERROR_INLINE_VAST_PROCESS]: 'rgb(139, 69, 19)', // Saddle Brown
  [STATUS_NAMES.LOCAL_PENALTY]: 'rgb(218, 112, 214)', // Orchid
  [STATUS_NAMES.ASSET_REJECTION]: 'rgb(173, 216, 230)', // Light Blue
  [STATUS_NAMES.SENT]: 'rgb(64, 224, 208)', // Turquoise
  [STATUS_NAMES.ASSET_PUBLISHER_UNAPPROVED]: 'rgb(100, 149, 237)', // Cornflower Blue
  [STATUS_NAMES.PROGRAMMATIC_GUARANTEED_DEAL_MISMATCH]: 'rgb(72, 61, 139)', // Dark Slate Blue
  [STATUS_NAMES.MISSING_ASSET_NOT_LINKED_TO_AD]: 'rgb(199, 21, 133)', // Medium Violet Red
  // New Event Types
  [STATUS_NAMES.ASSET_MISSING]: 'rgb(112, 128, 144)', // Slate Gray
  [STATUS_NAMES.ASSET_REJECTED]: 'rgb(119, 136, 153)', // Light Slate Gray
  [STATUS_NAMES.ASSET_UNQUALIFIED]: 'rgb(176, 196, 222)', // Light Steel Blue
  [STATUS_NAMES.ASSET_MISSING_NOT_LINKED_TO_AD]: 'rgb(198, 214, 232)', // Pale Steel Blue
  [STATUS_NAMES.COMPANION_INVALID_API_FRAMEWORK]: 'rgb(95, 158, 160)', // Cadet Blue
  [STATUS_NAMES.COMPANION_INVALID_TYPE]: 'rgb(132, 112, 255)', // Light Slate Blue
  [STATUS_NAMES.COMPANION_MISSING]: 'rgb(123, 104, 238)', // Medium Slate Blue
  [STATUS_NAMES.COMPANION_REGISTERED_NOT_FOUND]: 'rgb(106, 90, 205)', // Slate Blue
  [STATUS_NAMES.FETCH_FAILED]: 'rgb(220, 20, 60)', // Crimson\
  [STATUS_NAMES.INTERNAL_INVALID_VAST_URL]: 'rgb(188, 143, 143)', // Rosy Brown
  [STATUS_NAMES.INTERNAL_NO_VAST_URL]: 'rgb(205, 92, 92)', // Indian Red
  [STATUS_NAMES.INTERNAL_NULL_VAST_FROM_AD_PROVIDER]: 'rgb(178, 34, 34)', // Firebrick
  [STATUS_NAMES.INVALID_DOCUMENT_EMPTY_AD]: 'rgb(233, 150, 122)', // Dark Salmon
  [STATUS_NAMES.INVALID_DOCUMENT_INVALID_CLICKTHROUGH_URL]: 'rgb(153, 50, 204)', // Dark Orchid
  [STATUS_NAMES.INVALID_DOCUMENT_NULL_RAW]: 'rgb(85, 107, 47)', // Dark Olive Green
  [STATUS_NAMES.INVALID_DOCUMENT_PG_DEAL_MISMATCH]: 'rgb(189, 183, 107)', // Dark Khaki
  [STATUS_NAMES.INVALID_DOCUMENT_PG_DEAL_NAME_MISSING]: 'rgb(184, 134, 11)', // Dark Goldenrod
  [STATUS_NAMES.INVALID_DOCUMENT_UNMARSHALLER_EXCEPTION]: 'rgb(47, 79, 79)', // Dark Slate Gray
  [STATUS_NAMES.INVALID_DOCUMENT_UNPARSEABLE_DOCUMENT]: 'rgb(143, 188, 143)', // Dark Sea Green
  [STATUS_NAMES.INVALID_DOCUMENT_UNWRAPPING_FAILURE]: 'rgb(72, 61, 139)', // Dark Slate Blue
  [STATUS_NAMES.LOCAL_PENALTY]: 'rgb(218, 112, 214)', // Orchid
  [STATUS_NAMES.MISSING_ASSET_NOT_LINKED_TO_AD]: 'rgb(199, 21, 133)', // Medium Violet Red
  [STATUS_NAMES.PROGRAMMATIC_GUARANTEED_DEAL_MISMATCH]: 'rgb(75, 0, 130)', // Indigo
  // HTTP Related
  [STATUS_NAMES.HTTP_REASON_BAD_GATEWAY]: 'rgb(210, 105, 30)', // Chocolate
  [STATUS_NAMES.HTTP_REASON_BAD_REQUEST]: 'rgb(244, 164, 96)', // Sandy Brown
  [STATUS_NAMES.HTTP_REASON_FORBIDDEN]: 'rgb(220, 88, 42)', // Burnt Orange
  [STATUS_NAMES.HTTP_REASON_INTERNAL_SERVER_ERROR]: 'rgb(160, 82, 45)', // Sienna
  [STATUS_NAMES.HTTP_REASON_OK]: 'rgb(60, 179, 113)', // Medium Sea Green
  [STATUS_NAMES.HTTP_REASON_REQUEST_TIMEOUT]: 'rgb(70, 130, 180)', // Steel Blue
  [STATUS_NAMES.HTTP_REASON_SERVICE_UNAVAILABLE]: 'rgb(186, 85, 211)', // Medium Orchid
};
